import { Link } from "react-router";
import { ArrowUpRight, Clock, ShoppingBag, TrendingUp } from "lucide-react";
import { getErrorMessage } from "@/lib/errors";
import { useOrdersQuery, usePrefetchOrderQuery } from "@/lib/queries";

export function Dashboard() {
  const { data, isPending, isError, error, refetch } = useOrdersQuery("all");
  const prefetchOrder = usePrefetchOrderQuery();
  const orders = data ?? [];

  const pending = orders.filter((o: any) => o.status === "pending");
  const confirmed = orders.filter((o: any) => o.status === "confirmed");
  const recent = orders.slice(0, 5);

  const stats = [
    {
      label: "Pending",
      value: isPending ? "--" : String(pending.length),
      sub: "Awaiting confirmation",
      icon: Clock,
    },
    {
      label: "Confirmed",
      value: isPending ? "--" : String(confirmed.length),
      sub: "In progress",
      icon: TrendingUp,
    },
    {
      label: "Total orders",
      value: isPending ? "--" : String(orders.length),
      sub: "All time",
      icon: ShoppingBag,
    },
  ];

  return (
    <div className="space-y-10">
      {/* Header */}
      <div>
        <h1 className="text-[22px] font-semibold tracking-tight">Overview</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Your bakery at a glance.
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="group relative rounded-lg border bg-background p-5 transition-colors hover:bg-muted/50"
          >
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-medium uppercase tracking-wider text-muted-foreground">
                {stat.label}
              </span>
              <stat.icon className="size-3.5 text-muted-foreground/60" />
            </div>
            <p className="mt-2 text-[28px] font-semibold tracking-tight">
              {stat.value}
            </p>
            <p className="mt-0.5 text-[12px] text-muted-foreground">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Recent orders */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[14px] font-semibold">Recent orders</h2>
          <Link
            to="/orders"
            className="group flex items-center gap-1 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            View all
            <ArrowUpRight className="size-3 transition-transform group-hover:-translate-y-px group-hover:translate-x-px" />
          </Link>
        </div>

        {isError ? (
          <div className="flex h-32 flex-col items-center justify-center gap-3 rounded-lg border">
            <p className="text-[13px] text-muted-foreground">{getErrorMessage(error)}</p>
            <button
              onClick={() => refetch()}
              className="inline-flex h-8 items-center rounded-md border px-3 text-[12px] font-medium transition-colors hover:bg-muted"
            >
              Retry
            </button>
          </div>
        ) : isPending ? (
          <div className="flex h-32 items-center justify-center rounded-lg border">
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
          </div>
        ) : recent.length === 0 ? (
          <div className="flex h-32 items-center justify-center rounded-lg border">
            <p className="text-[13px] text-muted-foreground">No orders yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                    Customer
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Item
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Pickup
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {recent.map((order: any, i: number) => (
                  <tr
                    key={order.id}
                    className="border-b last:border-0 transition-colors hover:bg-muted/20"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/orders/${order.id}`}
                        onMouseEnter={() => prefetchOrder(order.id)}
                        onFocus={() => prefetchOrder(order.id)}
                        onTouchStart={() => prefetchOrder(order.id)}
                        className="text-[13px] font-medium underline-offset-4 hover:underline"
                      >
                        {order.customer_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-muted-foreground">
                      {order.item_name}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-muted-foreground">
                      {order.pickup_date}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={order.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: "bg-foreground/5 text-foreground/70",
    confirmed: "bg-foreground/10 text-foreground/90",
    completed: "bg-foreground text-background",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
        styles[status] ?? styles.pending
      }`}
    >
      {status}
    </span>
  );
}
